import { OrderState, OrderStatusGroup, isOrderStatusGroupLocked } from '@tcr-erp/shared';

const CANCEL_EXCLUDED_GROUPS: OrderStatusGroup[] = [
  OrderStatusGroup.SHIPPING,
  OrderStatusGroup.COMPLETED,
  OrderStatusGroup.RETURNED,
  OrderStatusGroup.CANCELLED,
];

/**
 * Nút "Hủy đơn" ẩn khi đơn đã Huỷ/Hoàn tiền hoặc đã Hoàn thành (TCR-1097),
 * HOẶC đã sang nhóm giao hàng/kết thúc theo `OrderStatusGroup` (TCR-1094
 * vòng 2 — QA yêu cầu KHÔNG cho Huỷ ở nhóm "Đang giao" dù `state` vẫn là
 * DELIVERING xuyên suốt cả hành trình giao hàng). `statusGroup` optional để
 * không phá vỡ nơi gọi cũ chỉ có `state` trong tay.
 */
export function canCancelOrder(state: OrderState, statusGroup?: OrderStatusGroup | null): boolean {
  if (state === OrderState.CANCELED || state === OrderState.REFUNDED || state === OrderState.COMPLETED) {
    return false;
  }
  if (statusGroup && CANCEL_EXCLUDED_GROUPS.includes(statusGroup)) {
    return false;
  }
  return true;
}

/** Nút "Đổi trả hàng" chỉ hiện khi đơn đã Hoàn thành (TCR-1097). */
export function canReturnOrder(state: OrderState): boolean {
  return state === OrderState.COMPLETED;
}

/**
 * Nút "Sửa đơn" hiện khi đơn còn PENDING (ai cũng sửa được), HOẶC người thao tác
 * có quyền `orders.update` và đơn chưa Huỷ/Hoàn tiền (TCR-1094). Phụ thuộc CẢ
 * `state` lẫn quyền hiện tại — không phải hàm thuần chỉ theo state như
 * `canCancelOrder`.
 *
 * RBAC fix (2026-09-01, audit "nv_kho/inventory tham gia toàn bộ chu trình đơn
 * hàng, CHỈ TRỪ duyệt đơn + tạo đơn") — đổi tham số thứ 2 từ `role` (hardcode
 * `['owner','manager']`) sang `canUpdateOrder: boolean` (kết quả
 * `hasPermission('orders.update')` do nơi gọi tự tính) để tự ăn theo quyền BE
 * cấp, không hardcode danh sách role cứng ở FE nữa — BE giờ đã cấp
 * `orders.update` cho cả `inventory`/`nv_kho`.
 *
 * TCR-1963 — Nháp sửa được như Chờ xác nhận (ai cũng sửa được, không cần
 * `orders.update`): đơn Nháp là bản POS chưa hoàn tất, cần sửa tự do trước khi
 * gửi xác nhận, giống PENDING.
 */
export function canEditOrder(state: OrderState, canUpdateOrder: boolean): boolean {
  return (
    state === OrderState.PENDING ||
    state === OrderState.DRAFT ||
    (canUpdateOrder && ![OrderState.CANCELED, OrderState.REFUNDED].includes(state))
  );
}

export const ORDER_STATUS_LOCK_TOOLTIP =
  'Đơn đã xác nhận — bị khoá sửa/huỷ. Cần quyền "Sửa/huỷ đơn đã khoá theo trạng thái".';

/**
 * TCR-1964 — tầng khoá thứ 2 theo trạng thái, cùng luật BE (`assertOrderNotStatusLocked`): từ
 * "Mới" trở đi khoá sửa/huỷ/tách với mọi role trừ ai có `orders.override_lock`. Tách riêng khỏi
 * `canEditOrder`/`canCancelOrder` để nơi gọi phân biệt "không áp dụng" (ẩn) với "bị khoá"
 * (disable + tooltip, người dùng biết đường xin quyền).
 */
export function isOrderLockedForUser(
  statusGroup: OrderStatusGroup | string | null | undefined,
  canOverrideLock: boolean,
): boolean {
  return !canOverrideLock && isOrderStatusGroupLocked(statusGroup);
}
